import * as SSO from "@10x/sso";
import { getParticipantID } from "@10x/sso";
import React, { ChangeEvent, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ProcedureDocs, ProcedureDocument, ProcedureParty, PropertyProcedure } from "@10x/dubai-rest-api/dist/property_registry/entity";
import { LocaleUtil, StringUtil } from "@eres/core";
import { PropertyRegistryApi } from "@10x/dubai-rest-api";
import { SpinnerLoader } from "@my-dld/components/SpinnerLoader";
import Strings from "@my-dld/assets/strings";
import { formatAppDate } from "@my-dld/utils/Utils";
import PageContent from "@my-dld/components/PageContent";
import PageTitle from "@my-dld/components/PageTitle";
import { DashboardBCItem, TabuPropertyBCItem } from "@my-dld/contexts/PageContext";
import { filterObjects, matchObject } from "@10x/dubai-rest-api/dist/property_registry/utils";
import { isEmailVerified } from "@my-dld/utils/User";
import { hideLoading, showLoading, showMessage, showUnverifiedEmailError } from "@my-dld/utils/UIUtils";
import { RePrintRequestModal } from "../../apis/financeDep/entities";
import * as FinanceDepApi from "../../apis/financeDep/api";
import { trackEventWithData } from "@my-dld/utils/Analytics";
import { downloadFile } from "@my-dld/utils/UrlUtils";

const MAX_NO_OF_DOCUMENTS_TO_VIEW = 3;

export function PropertyProcedures( props: { propertyId: string | number; query: string } ) {
	let { propertyId, query } = props;
	const ownerId = getParticipantID();
	const [ isLoading, setLoading ] = useState( false );
	const [ procedures, setProcedures ] = useState<PropertyProcedure[]>( [] );
	useEffect( () => {
		if ( ownerId ) {
			setLoading( true );
			PropertyRegistryApi.getPropertyProcedures( propertyId )
			.then( x => {
				x = x.sort( ( a: PropertyProcedure, b: PropertyProcedure ) => {
					const av = a.instanceDate.asDate().getTime();
					const bv = b.instanceDate.asDate().getTime();
					return bv - av;
				} );
				setProcedures( x );
				setLoading( false );
			} ).catch( e => {
				setLoading( false );
			} );
		}
	}, [ ownerId, propertyId ] );
	return <>
		{ isLoading && <div className={ "d-flex " }><SpinnerLoader/></div> }
		{ procedures && procedures.length > 0 && <div className="row">
			{ procedures.map( ( p, ix ) =>
				<ProcedureItem procedure={ p } key={ ix } query={ query }/> ) }
		</div>
		}
		{ !isLoading && ( procedures == null || procedures.length === 0 ) &&
			<div className="row">
				<div className="col-xl-12 col-lg-12 col-md-12 col-sm-12 col-12">
					<div className="alert alert-warning">{ Strings.no_data_found }</div>
				</div>
			</div>
		}
	</>;
}

export function ProcedureItem( props: { procedure: PropertyProcedure; query: string } ) {
	let { procedure, query } = props;
	let { instanceDate, name, procedureId, procedureNumber, procedureYear, propertyId } = procedure;
	const [ docs, setDocs ] = useState<ProcedureDocs | undefined>( procedure.info );
	const [ displayDocs, setDisplayDocs ] = useState<ProcedureDocument[] | undefined>( procedure.info?.documents );
	const [ isLoading, setLoading ] = useState( false );
	const [ docLoaded, setDocLoaded ] = useState( false );
	const [ showMe, setShowMe ] = useState( true );
	const [ hasDocs, setHasDocs ] = useState( false );
	const [ showViewMore, setViewMore ] = useState( false );
	const [ isExpand, setExpandProcedure ] = useState( false );
	useEffect( () => {
		const isMatch = matchObject( procedure, query ?? "" );
		setShowMe( isMatch );
	}, [ procedure, query, procedure.info ] );
	useEffect( () => {
		if ( docs ) {
			const filtered = filterObjects( docs.documents, query ?? "" );
			let m = [];
			if ( filtered.length !== docs.documents.length ) {
				m = filtered;
			} else {
				m = docs.documents;
			}
			setViewMore( m != undefined && m?.length > MAX_NO_OF_DOCUMENTS_TO_VIEW );
			if ( isExpand ) {
				setDisplayDocs( m );
			} else {
				const display = m.slice( 0, MAX_NO_OF_DOCUMENTS_TO_VIEW );
				setDisplayDocs( display );
			}
		}
	}, [ docs, query, isExpand ] );
	useEffect( () => {
		setHasDocs( displayDocs != undefined && displayDocs?.length > 0 );
	}, [ displayDocs ] );
	const getDocs = () => {
		setLoading( true );
		PropertyRegistryApi.getProcedureDocuments( propertyId, `${ procedureId }-${ procedureNumber }-${ procedureYear }` )
		.then( x => {
			setDocLoaded( true );
			procedure.info = x;
			setDocs( x );
			setLoading( false );
		} )
		.catch( e => {
			setDocLoaded( true );
			setDocs( undefined );
			setLoading( false );
		} );
	};
	useEffect( () => {
		getDocs();
	}, [] );

	const sendReceipt = () => {
		// sendMail();
		// return;
		if ( isEmailVerified() ) {
			sendMail();
		} else {
			showUnverifiedEmailError();
		}
	};

	const sendMail = () => {
		const request: RePrintRequestModal = {
			participantId: SSO.getParticipantID()!,
			procedureType: procedure.procedureId,
			procedureNumber: procedure.procedureNumber,
			procedureYear: procedure.procedureYear,
			email: SSO.getEmail(),
		};
		if ( request.email && request.email !== "" ) {
			showLoading();
			FinanceDepApi.sendMailReceipt( request ).then( resp => {
				hideLoading();
				trackEventWithData( "ResendReceipt", "Success", { email: request.email, source: "OwnedPropertyService" } );
				showMessage( Strings.receipt_sent_to_profile_email );
			} ).catch( ex => {
				hideLoading();
				trackEventWithData( "ResendReceipt", "Failed", { email: request.email, source: "OwnedPropertyService" } );
				showMessage( ex.toString() );
			} );
		}
	};
	const toggleExpansion = () => {
		if ( showViewMore ) {
			setExpandProcedure( !isExpand );
		}
	};
	if ( !showMe ) return <></>;
	return <div className="col-xl-6 col-lg-6 col-md-6 col-sm-12 col-12 propperty-document">
		<div className="card">
			<div className="card-body">
				<div className="document-archive-wrap">
					<div className="card-title">{ LocaleUtil.getLocalValue( name ) }
						<br/> { Strings.procedure_number } - { procedureNumber + "/" + procedureYear }<br/>
						<small
							className="font-size12"><b>{ Strings.date }</b> : { formatAppDate( instanceDate ) }
						</small>
					</div>
                    <button type="button" className="btn btn-grey btn-sm" onClick={sendReceipt}><span className="material-icons-outlined sr-only">send</span><span className="">{ Strings.send_receipt }</span></button>
				</div>
			</div>
			<div className="card-footer more-document">
				{ isLoading && <div className={ "d-flex " }><SpinnerLoader/></div> }
				{ !isLoading && docLoaded && <>
					{ !hasDocs && <>
                        <span
	                        className="no-data-available font-size14 font-weight-bold">{ Strings.no_data_available }</span>
					</> }
					{ hasDocs && <>
						{ displayDocs?.map( ( doc, ix ) =>
							<ProcedureDocumentView document={ doc } key={ ix + "x" }/> ) }
					</> }
				</> }
			</div>
			{
				showViewMore && !isExpand &&
				<button className="btn btn-see-more" onClick={ toggleExpansion }><span>{ Strings.view_more }</span></button>
			}
			{
				showViewMore && isExpand &&
				<button className="btn btn-see-more" onClick={ toggleExpansion }><span>{ Strings.view_less }</span></button>
			}
		</div>
	</div>;
}

function ProcedureDocumentView( props: { document: ProcedureDocument } ) {
	let { document } = props;
	let url = document.url;
	if ( StringUtil.isEmptyStringOrNull( url ) ) {
		return <></>;
	}
	const download = () => {
		let x = url;
		if ( x.startsWith( "/" ) ) {
			x = "https://ameentasjeel.dubailand.gov.ae" + x;
			window.open( x );
		} else {
			downloadFile(x);
		}
	};
	return <div>
		<div className="btn-link doc-blue-link" onClick={ download }>{ LocaleUtil.getLocalValue( document.title ) }
			<span>{ Strings.download }</span></div>
		<span className="font-size14 hide">
            <small className="text-muted"><b>Size</b>: 9 MB </small>
            <small className="text-muted"><b>File</b>: PDF</small>
        </span>
	</div>;
}

function ProcedurePartyView( props: { party: ProcedureParty } ) {
	let { party } = props;
	return <div className={ "pa-proc-party" }>
		<div className={ "pa-proc-party-container" }>
			<div className={ "pa-proc-party-title-container" }>
				<div className={ "pa-proc-party-title" }>{ LocaleUtil.getLocalValue( party.name ) }</div>
				<div className={ "pa-proc-party-title-type" }>{ LocaleUtil.getLocalValue( party.type ) }</div>
			</div>
		</div>
		{ party?.documents?.map( ( doc, ix ) => <ProcedureDocumentView document={ doc } key={ ix + "x" }/> ) }
	</div>;
}

export default function PropertyDocuments() {
	const { propertyId, propertyTypeId } = useParams<any>();
	const [ query, setQuery ] = useState( "" );
	const onQueryChange = ( e: ChangeEvent<HTMLInputElement> ) => {
		setQuery( e.target.value );
	};
	return <PageContent title={ Strings.property_documents }>
		<PageTitle title={ Strings.property_documents } paths={ [ DashboardBCItem, TabuPropertyBCItem( propertyTypeId, propertyId ) ] }/>
        <div className="position-relative w-100 mb-3">
            <label className="sr-only" htmlFor="mydldPropServicelist">{Strings.search}</label>
            <input type="text" id="mydldPropServicelist" className="form-control search-field-icon" onChange={onQueryChange} placeholder={Strings.search} />
            <button className="btn txt-clear-btn" type="button"><i className="material-icons-outlined">close</i></button>
        </div>
		<PropertyProcedures propertyId={ propertyId } query={ query }/>
	</PageContent>;
}
